import { useRouteLoaderData } from 'react-router';

import type { RedemptionModalOrderData } from '@shared/dream-components';

import useGDPRTranslations from '~/components/DreamBuilder/nodes/hooks/useGDPRTranslations';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { ToastData } from '~/hooks/useToast';
import type { Product } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import getProductVariantCoachingSessionDetails from '~/utils/getProductVariantCoachingSessionDetails';
import getProductVariantWebinarDetails from '~/utils/getProductVariantWebinarDetails';

import useFreeProductCheckoutModalTranslations from '../_hooks/useFreeProductCheckoutModalTranslations';
import GuestCheckoutModal from './GuestCheckoutModal';
import GuestCoachingCheckoutModal from './GuestCoachingCheckoutModal';
import GuestWebinarCheckoutModal from './GuestWebinarCheckoutModal';
import LoggedInCheckoutModal from './LoggedInCheckoutModal';

interface Props {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: { order?: RedemptionModalOrderData; toast?: ToastData; redemptionToken?: string }) => void;
}

export const FreeProductCheckoutModal = ({ product, isOpen, onClose, onSuccess }: Props) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const gdprTranslations = useGDPRTranslations();
  const isLoggedIn = useIsLoggedIn();
  const translations = useFreeProductCheckoutModalTranslations({
    isLoggedIn,
    productCtaText: product.cta_text,
  });

  const productVariantId = product.default_product_variant?.id || '';
  const form = product.default_product_variant?.required_form ?? undefined;
  const { hasCalComScheduling } = getProductVariantWebinarDetails(product);
  const { isCoachingSession, coachingSessionProvider } = getProductVariantCoachingSessionDetails(product);

  if (isLoggedIn) {
    return (
      <LoggedInCheckoutModal
        product={product}
        productVariantId={productVariantId}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
        site={site}
        gdprTranslations={gdprTranslations}
        translations={translations}
        form={form}
      />
    );
  }

  if (hasCalComScheduling) {
    return (
      <GuestWebinarCheckoutModal
        product={product}
        productVariantId={productVariantId}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  if (isCoachingSession && coachingSessionProvider === 'cal_com') {
    return (
      <GuestCoachingCheckoutModal
        product={product}
        productVariantId={productVariantId}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <GuestCheckoutModal
      productVariantId={productVariantId}
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      site={site}
      gdprTranslations={gdprTranslations}
      translations={translations}
      form={form}
    />
  );
};
